// Tutorial.jsx – Schritt-für-Schritt-Einführung für Kunden
// Hebt jeweils ein Element hervor (über data-tour="…") und zeigt dazu eine Erklärung.
// Ist das Element (noch) nicht sichtbar, erscheint die Erklärung in der Mitte.
import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { PersonCard } from './PersonCard';
import './Tutorial.css';

// ── Beispielbilder ──────────────────────────────────────────────────────
// Kleine Vorführ-Karten für die Einführung (echte PersonCard, aber ohne Funktion).
const noop = () => {};
const DEMO_COLORS = ['#1540dc', '#ff9600', '#09ad01'];

function DemoCard({ name, gender = 'female', colorInfo, showControls = false, highlight, sub }) {
  return (
    <div className="tut-demo-card">
      <div className="tut-demo-card-inner">
        <PersonCard
          person={{ id: name, name, gender, maidenName: sub || '', birthDate: '', photo: null }}
          selected={false}
          onClick={noop}
          onEdit={null}
          onQuickAdd={showControls ? noop : null}
          onColorChange={showControls ? noop : null}
          colorInfo={colorInfo}
          forceShowControls={showControls}
          style={{ left: 0, top: 0 }}
        />
        {highlight === 'plus' && <div className="tut-ring tut-ring--plus" />}
        {highlight === 'color' && <div className="tut-ring tut-ring--color" />}
      </div>
    </div>
  );
}

function IllustrationPersonForm() {
  const rows = [
    ['Foto', 'empfohlen – danach öffnet sich der Bildeditor: Ausschnitt wählen, drehen, automatisch verbessern'],
    ['Vorname * und Nachname', 'der Vorname steht auf der Fotokarte, Vor- und Nachname auf der Namenskarte'],
    ['Ledigname', 'Geburtsname, z.B. bei verheirateten Personen'],
    ['Geschlecht', 'männlich, weiblich oder unbekannt'],
    ['Geburtsdatum', 'optional'],
    ['Beziehungen', 'wie die Person mit den bestehenden verwandt ist: Kind, Elternteil, Geschwister, Partner oder Expartner von …'],
  ];
  return (
    <div className="tut-form-mock">
      {rows.map(([label, hint]) => (
        <div key={label} className="tut-form-row">
          <div className="tut-form-label">{label}</div>
          <div className="tut-form-hint">{hint}</div>
        </div>
      ))}
    </div>
  );
}

function IllustrationCard() {
  return (
    <div className="tut-demo-row">
      <DemoCard name="Max Muster" gender="male" />
    </div>
  );
}

function IllustrationPlus() {
  return (
    <div className="tut-demo-row">
      <DemoCard name="Anna Muster" showControls highlight="plus" />
      <div className="tut-menu-mock">
        {['Kind', 'Elternteil', 'Geschwister', 'Partner', 'Expartner'].map(l => <div key={l}>{l}</div>)}
      </div>
    </div>
  );
}

function IllustrationColors() {
  return (
    <div className="tut-demo-row tut-demo-row--three">
      <div className="tut-demo-col">
        <DemoCard name="Grossmutter" colorInfo={{ borderColors: DEMO_COLORS }} />
        <div className="tut-demo-caption">Oberste Generation:<br />mehrfarbiger Rahmen</div>
      </div>
      <div className="tut-demo-col">
        <DemoCard name="Tochter" colorInfo={{ color: DEMO_COLORS[0] }} />
        <div className="tut-demo-caption">Nächste Generation:<br />eine Farbe pro Familie</div>
      </div>
      <div className="tut-demo-col">
        <DemoCard name="Enkelin" colorInfo={{ symbols: ['square'], color: DEMO_COLORS[0] }} />
        <div className="tut-demo-caption">Darunter:<br />Symbol in der Familienfarbe</div>
      </div>
    </div>
  );
}

function IllustrationColorButton() {
  return (
    <div className="tut-demo-row">
      <DemoCard name="Peter Muster" gender="male" colorInfo={{ color: DEMO_COLORS[1], isGen1: true }} showControls highlight="color" />
    </div>
  );
}

// Welche Illustration zu welchem Schritt gehört (Feld „illustration“ in buildSteps).
const ILLUSTRATIONS = {
  form: IllustrationPersonForm,
  card: IllustrationCard,
  plus: IllustrationPlus,
  colors: IllustrationColors,
  colorButton: IllustrationColorButton,
};

// ── Schritte ────────────────────────────────────────────────────────────
// Alle Schritte der Einführung. Jeder Schritt hat:
//   title / text    – Überschrift und Erklärung
//   target          – Liste von data-tour-Namen; das erste sichtbare Element wird hervorgehoben
//   illustration    – stattdessen eine Vorführ-Grafik zeigen
//   needsPersons    – nur hervorheben, wenn schon Personen im Stammbaum sind
//   ownerOnly       – Schritt nur für Besitzer (z.B. Bestellen), nicht für Helfer
// Neuer Schritt? Einfach in dieser Liste ergänzen.
function buildSteps({ loggedIn, helper }) {
  const steps = [
    helper ? {
      title: 'Willkommen als Helfer!',
      text: <>Du hilfst bei diesem Stammbaum mit. In wenigen Schritten zeigen wir dir, wie du Personen
        und Fotos ergänzt. Du kannst die Einführung jederzeit über das <strong>„?“</strong> oben wieder starten.</>,
    } : {
      title: 'Willkommen bei Stammbaum-Memo!',
      text: <>In wenigen Schritten zeigen wir dir, wie du deinen Stammbaum erstellst.
        Du kannst die Einführung jederzeit über das <strong>„?“</strong> oben wieder starten.</>,
    },
    {
      target: ['add-person'],
      title: 'Personen hinzufügen',
      text: <>Mit <strong>„+ Person“</strong> fügst du eine neue Person hinzu. Starte mit einer
        beliebigen Person – zum Beispiel mit dir selbst – und baue den Stammbaum von dort aus auf.</>,
    },
    {
      illustration: 'form',
      title: 'Das Fenster „Neue Person“',
      text: <>Hier erfasst du die Angaben zur Person:</>,
    },
    {
      illustration: 'card',
      title: 'Person ansehen und bearbeiten',
      text: <>Ein Klick auf eine Karte (auf dem Handy: antippen) öffnet die Details. Dort kannst du
        die Person <strong>bearbeiten</strong> oder <strong>nur diese eine Person löschen</strong>.
        Am Computer öffnet ein Doppelklick direkt das Bearbeiten.</>,
    },
    {
      illustration: 'plus',
      title: 'Verwandte direkt hinzufügen',
      text: <>Über das <strong>„+“ unter einer Karte</strong> fügst du direkt ein Kind, einen Elternteil,
        Geschwister oder einen (Ex-)Partner hinzu – die Beziehung ist dann schon ausgefüllt.
        Am Computer erscheint das „+“, wenn du mit der Maus über die Karte fährst.</>,
    },
    {
      illustration: 'colors',
      title: 'Farben und Symbole',
      text: <>Rahmenfarben und Symbole werden <strong>automatisch</strong> vergeben. So erkennt man
        auf einen Blick, wer zu welcher Familie gehört.</>,
    },
    {
      illustration: 'colorButton',
      title: 'Familienfarbe ändern',
      text: <>Bei den Geschwistern der ersten Generation unter den obersten Personen erscheint
        oben rechts ein <strong>Farbsymbol</strong>. Damit kannst du die automatisch gewählte
        Familienfarbe ändern.</>,
    },
    {
      target: ['zoom'],
      title: 'Navigation',
      text: <><strong>+ / −</strong> zoomt hinein und hinaus, die mittlere Taste zeigt wieder den ganzen
        Stammbaum. Verschieben kannst du ihn mit der Maus (ziehen) oder mit einem Finger (wischen);
        zoomen geht auch mit dem Mausrad oder mit zwei Fingern.</>,
    },
    {
      target: ['preview', 'more'],
      title: 'Vorschau der Karten',
      text: <>Hier siehst du alle Memo-Karten so, wie sie gedruckt werden – pro Person die
        <strong> Fotokarte und die Namenskarte</strong> nebeneinander. Ein Klick aufs Foto öffnet den Bildeditor.</>,
      mobileNote: 'Auf dem Handy findest du die Vorschau im Menü „⋯“.',
      missingNote: 'Dieser Button erscheint, sobald dein Stammbaum Personen enthält.',
      needsPersons: true,
    },
    {
      target: ['title'],
      title: 'Stammbaum umbenennen',
      text: <>Ein Klick auf den <strong>Namen</strong> deines Stammbaums, und du kannst ihn umbenennen.</>,
    },
    {
      target: ['back'],
      title: loggedIn ? '„Meine Stammbäume“' : 'Wechseln',
      text: loggedIn
        ? <>Hier kommst du zurück zur <strong>Übersicht deiner Stammbäume</strong>. Dort kannst du
            weitere Stammbäume öffnen oder neue erstellen.</>
        : <>Hier kommst du zurück zur Startseite.</>,
    },
    {
      target: ['save', 'more'],
      title: 'Speichern',
      text: <>Dein Stammbaum wird <strong>automatisch gespeichert</strong> – wenige Sekunden nach
        jeder Änderung. Mit diesem Button kannst du zusätzlich jederzeit selbst speichern.</>,
      mobileNote: 'Auf dem Handy findest du „Speichern“ im Menü „⋯“.',
    },
    {
      target: ['price', 'more'],
      title: 'Preis',
      text: <>Hier siehst du den <strong>ungefähren Preis</strong> deines Stammbaums – abhängig von der
        Anzahl Personen und der Anzahl Exemplare.</>,
      mobileNote: 'Auf dem Handy findest du „Preis“ im Menü „⋯“.',
      missingNote: 'Dieser Button erscheint, sobald dein Stammbaum Personen enthält.',
      needsPersons: true,
    },
    {
      ownerOnly: true,
      target: ['delete', 'more'],
      title: 'Stammbaum löschen',
      warning: true,
      text: <><strong>Achtung:</strong> Dieser Button löscht den <strong>gesamten Stammbaum</strong> mit
        allen Personen und Fotos – endgültig. Um nur eine einzelne Person zu entfernen, öffne ihre
        Karte und wähle dort „Löschen“.</>,
      mobileNote: 'Auf dem Handy findest du „Stammbaum löschen“ im Menü „⋯“.',
      missingNote: 'Dieser Button erscheint, sobald dein Stammbaum Personen enthält.',
      needsPersons: true,
    },
    {
      ownerOnly: true,
      target: ['order'],
      title: 'Stammbaum bestellen',
      text: <>Wenn dein Stammbaum fertig ist, bestellst du ihn hier. <strong>Wichtig:</strong> Bis zur
        Bestellung ist dein Stammbaum privat – BandeleArt kann ihn <strong>nicht einsehen</strong>.
        Nach der Bestellung wird er gesperrt und kann nur noch angeschaut werden.</>,
      missingNote: 'Dieser Button erscheint, sobald dein Stammbaum Personen enthält.',
      needsPersons: true,
    },
    {
      title: 'Gut zu wissen',
      text: (
        <ul className="tut-list">
          <li><strong>Fotos</strong> sind sehr empfohlen – sie werden beim Hochladen automatisch passend verkleinert.
            Im Bildeditor kannst du ein Foto für CHF 5.– auch <strong>von Hand bearbeiten lassen</strong>.</li>
          {helper && <li>Es kann immer nur <strong>eine Person gleichzeitig</strong> bearbeiten. Den Stammbaum
            <strong> löschen oder bestellen</strong> kann nur die Person, die dich eingeladen hat.</li>}
          <li>Es sind höchstens <strong>4 Generationen</strong> möglich, in der obersten Generation höchstens 2 Paare oder Einzelpersonen (z.B. beide Grosseltern-Paare).</li>
          <li>Nicht bestellte Stammbäume, die <strong>30 Tage</strong> nicht geöffnet werden, werden gelöscht. Du erhältst vorher eine Erinnerung per E-Mail.</li>
          <li>Bei Fragen: <a href="mailto:stammbaum-memo@bandeleart.ch">stammbaum-memo@bandeleart.ch</a></li>
        </ul>
      ),
    },
    {
      title: 'Viel Spass!',
      text: <>Jetzt kannst du loslegen. Die Einführung findest du jederzeit wieder über das
        <strong> „?“</strong>{' '}oben.</>,
    },
  ];
  return helper ? steps.filter(st => !st.ownerOnly) : steps;
}

// ── Hilfsfunktionen ─────────────────────────────────────────────────────
// Sucht das hervorzuhebende Element auf der Seite (Attribut data-tour="…") und
// liefert seine Lage. Unsichtbare Elemente (z.B. im „⋯“-Menü versteckt) werden übersprungen.
function findTarget(keys) {
  if (!keys) return null;
  for (const k of keys) {
    const els = document.querySelectorAll(`[data-tour="${k}"]`);
    for (const el of els) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden') return { key: k, rect: r };
    }
  }
  return null;
}

// ── Komponente ──────────────────────────────────────────────────────────
// Die Einführung selbst. index = aktueller Schritt, target = Lage des hervorgehobenen Elements.
export function Tutorial({ onClose, loggedIn, hasPersons = true, helper = false }) {
  const steps = React.useMemo(() => buildSteps({ loggedIn, helper }), [loggedIn, helper]);
  const [index, setIndex] = useState(0);
  const [target, setTarget] = useState(null);
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });
  const step = steps[index];
  const isLast = index === steps.length - 1;

  // Lage des Elements neu messen (beim Schrittwechsel und bei Grössenänderung des Fensters).
  const measure = useCallback(() => {
    setViewport({ w: window.innerWidth, h: window.innerHeight });
    setTarget(step.needsPersons && !hasPersons ? null : findTarget(step.target));
  }, [step, hasPersons]);

  useLayoutEffect(() => { measure(); }, [measure]);
  useEffect(() => {
    window.addEventListener('resize', measure);
    const t = setTimeout(measure, 150); // nach evtl. Layout-Änderungen nochmals messen
    return () => { window.removeEventListener('resize', measure); clearTimeout(t); };
  }, [measure]);

  const next = useCallback(() => { if (isLast) onClose(); else setIndex(i => i + 1); }, [isLast, onClose]);
  const prev = useCallback(() => setIndex(i => Math.max(0, i - 1)), []);

  // Tastatur: Escape beendet, Pfeil rechts/Enter weiter, Pfeil links zurück.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, onClose]);

  // Position der Erklärung
  const PAD = 6;
  const compact = viewport.w < 640;
  const hasIllustration = !!step.illustration;
  let cardStyle = {};
  let cardClass = 'tut-card';
  if (!target || hasIllustration) {
    cardClass += ' tut-card--center';
  } else {
    // Platz rund um das hervorgehobene Element messen und die Karte dorthin legen,
    // wo am meisten Platz ist – auch auf Handys im Querformat (wenig Höhe)
    const r = target.rect;
    const M = 10;
    const below = viewport.h - (r.bottom + PAD) - 2 * M;
    const above = (r.top - PAD) - 2 * M;
    const right = viewport.w - (r.right + PAD) - 2 * M;
    const leftSpace = (r.left - PAD) - 2 * M;
    const NEED = 220;
    const vertical = Math.max(below, above);
    const side = Math.max(right, leftSpace);
    if (compact && vertical >= 150) {
      // Handy hochkant: als Blatt am oberen oder unteren Rand
      cardClass += below >= above ? ' tut-card--sheet-bottom' : ' tut-card--sheet-top';
      cardStyle = { maxHeight: Math.max(below, above) };
    } else if (vertical >= NEED || (vertical >= 150 && side < 240)) {
      const width = Math.min(380, viewport.w - 2 * M);
      const left = Math.min(Math.max(M, r.left + r.width / 2 - width / 2), viewport.w - width - M);
      cardStyle = below >= above
        ? { top: r.bottom + PAD + M, left, width, maxHeight: below }
        : { bottom: viewport.h - r.top + PAD + M, left, width, maxHeight: above };
    } else if (side >= 240) {
      // wenig Höhe (Querformat): links oder rechts neben das Element
      const width = Math.min(380, side);
      const top = Math.min(Math.max(M, r.top - 20), Math.max(M, viewport.h - 2 * M - 260));
      cardStyle = right >= leftSpace
        ? { left: r.right + PAD + M, top, width, maxHeight: viewport.h - top - M }
        : { right: viewport.w - r.left + PAD + M, top, width, maxHeight: viewport.h - top - M };
    } else {
      cardClass += ' tut-card--center';
    }
  }

  const Illustration = step.illustration ? ILLUSTRATIONS[step.illustration] : null;
  const usedFallback = target && step.target && target.key !== step.target[0];

  return (
    <div className="tut-root" role="dialog" aria-modal="true" aria-label="Einführung">
      {/* Klicks auf die Seite während des Tutorials abfangen */}
      <div className={`tut-blocker${target && !hasIllustration ? '' : ' tut-blocker--dim'}`} />
      {target && !hasIllustration && (
        <div
          className="tut-spotlight"
          style={{
            top: target.rect.top - PAD,
            left: target.rect.left - PAD,
            width: target.rect.width + PAD * 2,
            height: target.rect.height + PAD * 2,
          }}
        />
      )}

      <div className={cardClass} style={cardStyle}>
        <button className="tut-close" onClick={onClose} aria-label="Einführung schliessen" title="Einführung beenden">×</button>
        <div className="tut-step-count">Schritt {index + 1} von {steps.length}</div>
        <h3 className={`tut-title${step.warning ? ' tut-title--warning' : ''}`}>{step.title}</h3>
        <div className={`tut-text${step.warning ? ' tut-text--warning' : ''}`}>{step.text}</div>
        {Illustration && <div className="tut-illustration"><Illustration /></div>}
        {usedFallback && step.mobileNote && <div className="tut-note">{step.mobileNote}</div>}
        {!target && step.target && step.missingNote && <div className="tut-note">{step.missingNote}</div>}

        <div className="tut-progress">
          {steps.map((_, i) => <span key={i} className={i === index ? 'active' : i < index ? 'done' : ''} />)}
        </div>

        <div className="tut-actions">
          {index === 0
            ? <button className="tut-btn tut-btn--ghost" onClick={onClose}>Überspringen</button>
            : <button className="tut-btn tut-btn--ghost" onClick={prev}>Zurück</button>}
          <button className="tut-btn tut-btn--primary" onClick={next}>
            {isLast ? 'Los geht’s' : 'Weiter'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Frage nach dem Erstellen eines neuen Stammbaums ─────────────────────
// Kleines Fenster „Möchtest du eine kurze Einführung?“ beim ersten Öffnen.
export function TutorialOffer({ onStart, onSkip, helper = false, treeName = '' }) {
  return (
    <div className="tut-root" role="dialog" aria-modal="true">
      <div className="tut-blocker tut-blocker--dim" />
      <div className="tut-card tut-card--center tut-card--offer">
        <h3 className="tut-title">{helper ? 'Schön, dass du mithilfst!' : 'Dein Stammbaum ist bereit!'}</h3>
        <p className="tut-text">
          {helper && <>Du kannst jetzt beim Stammbaum{treeName ? <> „{treeName}“</> : ''} mitarbeiten. </>}
          Möchtest du eine kurze Einführung? Wir zeigen dir in rund zwei Minuten alle wichtigen
          Funktionen. Du kannst sie auch später jederzeit über das „?“ oben starten.
        </p>
        <div className="tut-actions">
          <button className="tut-btn tut-btn--ghost" onClick={onSkip}>Überspringen</button>
          <button className="tut-btn tut-btn--primary" onClick={onStart}>Tutorial starten</button>
        </div>
      </div>
    </div>
  );
}
